/* ═══════════════════════════════════════════════════════════════════════════
   Client mirror of server/permissions.js — capability-based access control.

   The server is the source of truth and enforces every guarded endpoint. This
   module exists so the client can hide nav/routes the user can't use, and so a
   user object that predates the permissions feature (stale localStorage, no
   `permissions` map) still gates correctly by falling back to the role matrix.

   Prefer `can(user, cap)`:
     1. if user.permissions[cap] is set (fresh from /me or login), use it;
     2. otherwise derive from the role matrix + any permission_overrides.
   ═══════════════════════════════════════════════════════════════════════════ */

export const CAPABILITIES = [
  { key: 'users.manage',             label: 'Team Management',          group: 'Administration', description: 'Create, edit, and deactivate users; set roles and reporting lines.' },
  { key: 'settings.manage',          label: 'Settings',                 group: 'Administration', description: 'App settings and notification-list configuration.' },
  { key: 'audit.view',               label: 'Audit Log',                group: 'Oversight',      description: 'View the system-wide audit trail.' },
  { key: 'schedule_log.view',        label: 'Schedule Change Log',      group: 'Oversight',      description: 'View the cross-project schedule change history.' },
  { key: 'preconstruction.view',     label: 'Pre-Construction',         group: 'Operations',     description: 'View the pre-construction pipeline.' },
  { key: 'workload.view',            label: 'Workload',                 group: 'Operations',     description: 'View the team workload dashboard.' },
  { key: 'ar.view',                  label: 'AR / Collections',         group: 'Financials',     description: 'View accounts receivable and collections.' },
  { key: 'billings_projection.view', label: 'Billings Projection',      group: 'Financials',     description: 'View the all-projects billings projection.' },
  { key: 'jobcosts.view',            label: 'Job Costs (all projects)', group: 'Financials',     description: 'View the cross-project job-cost dashboard.' },
];

export const CAPABILITY_KEYS = CAPABILITIES.map((c) => c.key);

// VP shares Admin's rank — one top tier (same access, same org level).
const ROLE_RANK = { admin: 5, vp: 5, construction_director: 3, construction_manager: 2, pm: 1, e3_direct_director: 3, e3_direct_pm: 1, sales_manager: 1, sales_person: 1, project_developer: 1 };
const VIEW_CAPS = ['audit.view', 'schedule_log.view', 'preconstruction.view', 'workload.view', 'ar.view', 'billings_projection.view', 'jobcosts.view'];

// Role → default capability map. Mirrors server/permissions.js exactly.
export function defaultsForRole(roleKey) {
  const rank = ROLE_RANK[roleKey] || 0;
  const granted = {};
  for (const k of CAPABILITY_KEYS) granted[k] = false;
  if (rank >= ROLE_RANK.construction_manager) for (const k of VIEW_CAPS) granted[k] = true;
  if (rank >= ROLE_RANK.vp) granted['users.manage'] = true;
  if (rank >= ROLE_RANK.admin) granted['settings.manage'] = true;
  return granted;
}

export const ROLE_DEFAULTS = Object.keys(ROLE_RANK).reduce((acc, r) => {
  acc[r] = defaultsForRole(r);
  return acc;
}, {});

function cleanOverrides(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const clean = {};
  for (const [k, v] of Object.entries(raw)) {
    if (CAPABILITY_KEYS.includes(k) && typeof v === 'boolean') clean[k] = v;
  }
  return clean;
}

// Effective permission map for a user, with the same precedence as the server.
export function effectivePermissions(user) {
  if (!user) return {};
  const base = defaultsForRole(user.role);
  const overrides = cleanOverrides(user.permission_overrides);
  return { ...base, ...overrides };
}

// The one helper the rest of the app should use.
export function can(user, capability) {
  if (!user) return false;
  if (user.permissions && typeof user.permissions[capability] === 'boolean') {
    return user.permissions[capability];
  }
  return effectivePermissions(user)[capability] === true;
}
